import {
    type FC
} from "react";
import {
    View
} from "react-native";
import type IBubbleReactionsProps from "./type";
import stylesheet, {
    BUBBLE_REACTIONS_MAX_EMOJIS,
    useStyles
} from "./stylesheet";
import BubbleReactionsAdd from "./internal/add";
import BubbleReactionsChip from "./internal/chip";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    BubblePartContentProps
} from "../../type";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    withBubbleContext
} from "../../context";

const BubbleReactionsContent: FC<BubblePartContentProps<IBubbleReactionsProps>> = ({
    quickReactions: quickReactionsProp,
    onReactionAdd: onReactionAddProp,
    reactions: reactionsProp,
    onPress: onPressProp,
    align: alignProp,
    side: sideProp,
    customTheme,
    context,
    style,
    title,
    ...props
}) => {
    const currentTheme = customTheme ?? context.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const pointerType = usePointerType();

    const reactions = reactionsProp ?? context.reactions;

    const onPress = onPressProp ?? context.onReactionsPress;

    const onReactionAdd = onReactionAddProp ?? context.onReactionAdd;

    const quickReactions = quickReactionsProp ?? context.quickReactions;

    const side = sideProp ?? context.reactionsSide;

    const align = alignProp ?? (context.variant === "user" ? "end" : "start");

    const isInteractive = !!onReactionAdd;

    const isPressable = isInteractive || !!onPress;

    const isOwn = reactions.some((reaction) => reaction.isOwn);

    const total = reactions.reduce((sum, reaction) => sum + reaction.count, 0);

    const formatCount = (value: number) => formatNumber({
        locale: activeLocale,
        value
    });

    const formattedTotal = formatCount(total);

    const accessibleTitle = title ?? context.reactionsTitle ?? localize("reactions-total", [
        formattedTotal
    ]);

    const {
        container: containerDynamicStyle
    } = useStyles({
        marginHorizontal: context.variant === "plain" ? 0 : context.currentSize.paddingHorizontal,
        controlHeights,
        shapeConfigs,
        touchTargets,
        isPressable,
        pointerType,
        spaces,
        align,
        side
    });

    if(onReactionAdd) {
        const handleAdd = (emoji: string) => {
            onReactionAdd(emoji);
        };

        return <View
            aria-label={accessibleTitle}
            style={[
                style,
                stylesheet.container,
                stylesheet.row,
                containerDynamicStyle
            ]}
            role="group"
        >
            {
                reactions.map((reaction) => <BubbleReactionsChip
                    title={`${reaction.emoji} ${formatCount(reaction.count)}`}
                    onPress={() => handleAdd(reaction.emoji)}
                    countTitle={formatCount(reaction.count)}
                    customTheme={currentTheme}
                    isOwn={!!reaction.isOwn}
                    count={reaction.count}
                    emoji={reaction.emoji}
                    key={reaction.emoji}
                    isToggle={true}
                />)
            }
            <BubbleReactionsAdd
                customLocalize={context.customLocalize}
                quickReactions={quickReactions}
                customTheme={currentTheme}
                onSelect={handleAdd}
                align={align}
            />
        </View>;
    }

    return <BubbleReactionsChip
        emoji={reactions.slice(0, BUBBLE_REACTIONS_MAX_EMOJIS).map((reaction) => reaction.emoji).join("")}
        countTitle={formattedTotal}
        customTheme={currentTheme}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        title={accessibleTitle}
        onPress={onPress}
        count={total}
        isOwn={isOwn}
        {...props}
    />;
};
export default withBubbleContext("Reactions", BubbleReactionsContent);
